import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useEffect, useState } from "react";
import {
  BentoCategory,
  FollowUpQuestionOption,
  NicheFollowUpQuestion,
  OrganizationNiche,
} from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import { UserLocation } from "components/LocationAutocomplete/schema";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";

import NewNicheQuestions from "./NewNicheQuestions";
import SetTemplate from "./SetTemplate";

const styles = {
  drawer: {
    "& .MuiDrawer-paper": {
      position: "absolute",
      right: "-8px",
      boxShadow: "-8px 0px 16px rgba(0, 0, 0, 0.1)",
    },
  },
  editIcon: {
    fontSize: 14,
    color: grey[500],
  },
  closeIcon: {
    position: "absolute",
    right: "18px",
    top: "12px",
    fontSize: 18,
    color: grey[400],
  },
  link: {
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": {
      color: "primary.main",
      textDecoration: "underline",
    },
  },
};

interface IProps {
  addNewNiche: (niche: OrganizationNiche) => void;
  handleClose: (e?: CustomEvent) => void;
  open: boolean;
  categoriesToPickFrom: BentoCategory[];
  copiedNiche?: OrganizationNiche | null;
  copiedNicheTemplates?: Template[] | null;
}

type TemplateError = {
  idx: number;
  message: string;
};

export default function NewNicheDrawer({
  addNewNiche,
  open,
  handleClose,
  categoriesToPickFrom,
  copiedNiche,
  copiedNicheTemplates,
}: IProps) {
  const {
    templates: allTemplates,
    outreachTemplates,
    followupTemplates,
    setCreateTemplateDialogOpen,
    fetchOutreachTemplates,
    setCurrentTemplateContentType,
  } = useContext(OutreachTemplatesContext);
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { currentOrg, profile, emailSettings } = useContext(
    OrganizationUserContext,
  );
  const { getToken } = useAuth();

  const [page, setPage] = useState<number>(1);
  const [selectedCategory, setSelectedCategory] =
    useState<BentoCategory | null>(null);
  const [followUpQuestions, setFollowUpQuestions] = useState<
    NicheFollowUpQuestion[] | null
  >(null);

  const [selectedFollowupAnswers, setSelectedFollowupAnswers] = useState<{
    [key in number]: FollowUpQuestionOption[];
  }>({});
  const [templatesError, setTemplatesError] = useState<TemplateError | null>(
    null,
  );
  const [locationError, setLocationError] = useState<
    "city" | "location" | null
  >(null);
  const [nicheLocation, setNicheLocation] = useState<
    UserLocation | undefined
  >();
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(["en"]);
  const [modifiedTemplates, setModifiedTemplates] = useState<Template[]>([]);
  const [loadingCopiedNiche, setLoadingCopiedNiche] = useState<boolean>(false);

  const filteredOutreachTemplates = outreachTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const filteredFollowUps = followupTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const templateFromEmailSettings = emailSettings?.emailSettingTemplates?.map(
    (template) => template.templateId,
  );
  const selectedFollowups = modifiedTemplates?.slice(
    1,
    modifiedTemplates?.length,
  );

  const handleGenerateTemplate = () => {
    trackEvent("Generate Template Button Clicked", {
      Source: "VA Add Niche",
    });
    setCreateTemplateDialogOpen(true);
    setCurrentTemplateContentType(TemplateContentType.OUTREACH);
  };

  const handleTemplateChange = (e: CustomEvent, index: number) => {
    const { value } = e.target;
    const copy = [...modifiedTemplates];
    const template = allTemplates?.find((x) => x.id === value);
    if (template) {
      copy[index] = template;
      setModifiedTemplates(copy);
    }
  };

  const removeFollowUp = (index: number) => {
    const copy = [...modifiedTemplates];
    copy.splice(index, 1);
    setModifiedTemplates(copy);
  };

  const addFollowUp = () => {
    setModifiedTemplates([...modifiedTemplates, followupTemplates?.[0]]);
  };

  const initializeTemplates = () => {
    if (allTemplates?.length === 0) {
      fetchOutreachTemplates();
      return;
    }

    const presetTemplates = emailSettings?.emailSettingTemplates || [];
    const matchedTemplates = [];

    for (const settingTemplate of presetTemplates) {
      const template = allTemplates?.find(
        (x) => x.id === settingTemplate.templateId,
      );
      if (template) {
        matchedTemplates.push(template);
      }
    }
    if (matchedTemplates?.length > 0) {
      setModifiedTemplates(matchedTemplates);
      return;
    }

    if (filteredOutreachTemplates?.length > 0) {
      setModifiedTemplates([filteredOutreachTemplates[0]]);
    } else if (filteredOutreachTemplates?.length === 0) {
      fetchOutreachTemplates();
    }
  };

  useEffect(() => {
    initializeTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTemplates?.length]);

  const onClose = (e: CustomEvent) => {
    handleClose(e);
    setTimeout(() => {
      setPage(1);
      setSelectedCategory(null);
      setSelectedFollowupAnswers({});
      setFollowUpQuestions(null);
      setNicheLocation(undefined);
      setSelectedLanguages(["en"]);
      setTemplatesError(null);
      setLocationError(null);
      setLoadingCopiedNiche(false);
      initializeTemplates();
    }, 500);
  };

  const createNiche = async () => {
    setLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/organization-niches/with-automations`,
        "POST",
        {},
        {
          bentoCategoryId: selectedCategory?.id,
          templateIds: modifiedTemplates.map((template) => template.id),
          followUpResponses: selectedFollowupAnswers,
          nicheLocation,
          languages: selectedLanguages,
        },
        true,
      );

      const jsonMessage = await res.json();

      if (res.ok) {
        const { niche } = jsonMessage;
        addNewNiche(niche);
        handleClose();
        setTimeout(() => {
          setPage(1);
          setSelectedCategory(null);
          setSelectedFollowupAnswers({});
          setFollowUpQuestions(null);
          setNicheLocation(undefined);
          setSelectedLanguages(["en"]);
          setTemplatesError(null);
          setLocationError(null);
        }, 500);
      } else {
        if (jsonMessage.field) {
          if (
            jsonMessage.field === "templates" &&
            jsonMessage.templateIdx !== undefined &&
            jsonMessage.templateIdx !== null
          ) {
            setTemplatesError({
              idx: jsonMessage.templateIdx,
              message: jsonMessage.message,
            });
            return;
          }

          if (
            jsonMessage.field === "city" ||
            jsonMessage.field === "location"
          ) {
            setLocationError(jsonMessage.field);
            return;
          }
        }
        setAlert(jsonMessage.message, "error");
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const initializeLocation = () => {
    if (profile?.location) {
      setNicheLocation({
        address: profile?.city,
        longitude: profile?.longitude,
        latitude: profile?.latitude,
        country_code: profile?.location,
        address_components: profile?.addressComponents,
      });
    }
  };

  const handleLocationChange = (userLocation: UserLocation) => {
    if (userLocation.country_code) {
      setLocationError(null);
    }
    setNicheLocation(userLocation);
  };

  useEffect(() => {
    if (!nicheLocation && open) {
      initializeLocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Initialize from copied niche
  useEffect(() => {
    if (!open || !copiedNiche) return;
    if (!copiedNiche.bentoCategoryId) return;

    const category = categoriesToPickFrom.find(
      (c) => c.id === copiedNiche.bentoCategoryId,
    );
    if (!category) return;

    setSelectedCategory(category);

    setNicheLocation({
      address: copiedNiche.city,
      longitude: copiedNiche.longitude,
      latitude: copiedNiche.latitude,
      country_code: copiedNiche.countryCode,
      address_components: copiedNiche.addressComponents,
    });

    if (copiedNiche.followUpResponses?.length) {
      const answers: { [key in number]: FollowUpQuestionOption[] } = {};
      for (const response of copiedNiche.followUpResponses) {
        if (response.nicheFollowUpQuestionId && response.selectedOptions) {
          answers[response.nicheFollowUpQuestionId] = response.selectedOptions;
        }
      }
      setSelectedFollowupAnswers(answers);
    }

    if (copiedNiche.languages?.length) {
      setSelectedLanguages(copiedNiche.languages);
    }

    if (copiedNicheTemplates?.length) {
      setModifiedTemplates(copiedNicheTemplates);
    }

    // Fetch follow-up questions and decide which page to show
    const fetchAndSetPage = async () => {
      setLoadingCopiedNiche(true);
      try {
        const { followUpQuestions: questions } = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg?.id}/bento-categories/${category.id}/follow-up-questions`,
          "GET",
        );
        setFollowUpQuestions(questions);

        // If no follow-up questions, go directly to page 2
        if (!questions || questions.length === 0) {
          setPage(2);
        }
      } catch (error) {
        setErrorAlert(error);
      } finally {
        setLoadingCopiedNiche(false);
      }
    };

    fetchAndSetPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, copiedNiche]);

  return (
    <div>
      <Drawer open={open} anchor="right" onClose={onClose} sx={styles.drawer}>
        <Box
          sx={{
            p: 4,
            minWidth: "30vw",
            maxWidth: "500px",
          }}
        >
          <Typography variant="h4" sx={{ mr: 4, mt: 2 }}>
            New Niche
          </Typography>
          <IconButton sx={styles.closeIcon} onClick={onClose}>
            <Box component="span" className="fa-regular fa-xmark-large" />
          </IconButton>
          <Divider sx={{ my: 2 }} />
          {loadingCopiedNiche && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ py: 4 }}
            >
              <CircularProgress />
            </Box>
          )}
          {!loadingCopiedNiche && page === 1 && (
            <NewNicheQuestions
              goNext={() => {
                setPage(2);
                trackEvent("VA Settings Page Answered New Niche Questions");
              }}
              categoriesToPickFrom={categoriesToPickFrom}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              followUpQuestions={followUpQuestions}
              setFollowUpQuestions={setFollowUpQuestions}
              selectedFollowupAnswers={selectedFollowupAnswers}
              setSelectedFollowupAnswers={setSelectedFollowupAnswers}
            />
          )}
          {!loadingCopiedNiche && page === 2 && (
            <SetTemplate
              goBack={() => {
                setPage(1);
              }}
              modifiedTemplates={modifiedTemplates}
              handleTemplateChange={handleTemplateChange}
              filteredOutreachTemplates={filteredOutreachTemplates}
              filteredFollowUps={filteredFollowUps}
              templateFromEmailSettings={templateFromEmailSettings}
              selectedFollowups={selectedFollowups}
              removeFollowUp={removeFollowUp}
              addFollowUp={addFollowUp}
              followupTemplates={followupTemplates}
              loading={loading}
              handleGenerateTemplate={handleGenerateTemplate}
              templatesError={templatesError}
              createNiche={() => createNiche()}
              nicheLocation={nicheLocation}
              handleLocationChange={handleLocationChange}
              locationError={locationError}
              selectedLanguages={selectedLanguages}
              setSelectedLanguages={setSelectedLanguages}
            />
          )}
        </Box>
      </Drawer>
    </div>
  );
}
